import clsx from "clsx"

import { Trans, useTranslation } from "react-i18next"

interface GeoEditingBannerProps {
  geo: string // '*' or ISO-2
  isFork: boolean
  noun?: string
  className?: string
}

/**
 * Banner shown at the top of any geo-scoped editor (SectionOrderEditor,
 * HomepageOrderEditor) so it's impossible to miss which geo the admin is
 * currently editing, and whether it's a fork or just inheriting the default.
 *
 *   geo === '*'                  → green pill: editing global default
 *   geo !== '*' && isFork        → purple pill: editing the fork (active override)
 *   geo !== '*' && !isFork       → amber pill: inheriting the default, save will fork
 */
export const GeoEditingBanner = ({ geo, isFork, noun, className }: GeoEditingBannerProps) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const resolvedNoun = noun ?? t("gameManagement.geoBanner.defaultNoun", { defaultValue: "this order" })

  if (geo === "*") {
    return (
      <div
        className={clsx(
          "flex items-center gap-3 rounded-lg border px-4 py-3 text-sm",
          "border-emerald-500/30 bg-emerald-500/[0.07] text-emerald-700 dark:text-emerald-300",
          className
        )}
      >
        <span className="text-base" aria-hidden>
          🌐
        </span>
        <div className="flex flex-col leading-tight">
          <span>
            <Trans
              i18nKey="gameManagement.geoBanner.global"
              defaults="Editing <b>global default</b> — applies to every country without an override."
              components={{ b: <b /> }}
            />
          </span>
        </div>
      </div>
    )
  }

  if (isFork) {
    return (
      <div
        className={clsx(
          "flex items-center gap-3 rounded-lg border px-4 py-3 text-sm",
          "border-[rgba(132,_0,_168,_0.4)] bg-[rgba(132,_0,_168,_0.08)] text-[rgba(132,_0,_168,_1)] dark:text-[rgba(217,_140,_237,_1)]",
          className
        )}
      >
        <span className="text-base" aria-hidden>
          ✏️
        </span>
        <div className="flex flex-col leading-tight">
          <span>
            <Trans
              i18nKey="gameManagement.geoBanner.fork"
              defaults="Currently editing the <b>{{geo}}</b> fork."
              values={{ geo }}
              components={{ b: <b /> }}
            />
          </span>
          <span className="text-xs opacity-80">
            {t("gameManagement.geoBanner.forkHint", {
              defaultValue: "Changes apply to {{geo}} only. The global default is untouched.",
              geo
            })}
          </span>
        </div>
      </div>
    )
  }

  // !isFork && geo !== '*' — inherited default, save will create a fork
  return (
    <div
      className={clsx(
        "flex items-center gap-3 rounded-lg border px-4 py-3 text-sm",
        "border-amber-500/40 bg-amber-500/[0.08] text-amber-700 dark:text-amber-300",
        className
      )}
    >
      <span className="text-base" aria-hidden>
        ⚠️
      </span>
      <div className="flex flex-col leading-tight">
        <span>
          <Trans
            i18nKey="gameManagement.geoBanner.inherited"
            defaults="Viewing <b>{{geo}}</b> — inherited from the global default (no fork yet)."
            values={{ geo }}
            components={{ b: <b /> }}
          />
        </span>
        <span className="text-xs opacity-80">
          <Trans
            i18nKey="gameManagement.geoBanner.inheritedHint"
            defaults="Saving {{noun}} here will fork <b>{{geo}}</b> off so the changes apply to {{geo}} only."
            values={{ geo, noun: resolvedNoun }}
            components={{ b: <b /> }}
          />
        </span>
      </div>
    </div>
  )
}
